/* eslint-disable @typescript-eslint/ban-types */
import { ConnectData, CONTINUE_CHAT_URL, FeedBackParams, MessageSource, MessageStatus, MessageType, REGENERATE_CHAT_URL } from "./../services/api_question";
import {
  ChatMessage,
  ChatMessageEventSource,
  ChatMessageId,
  feedBackChat,
  generateAssociationWord,
  generateEmptyMessage,
  getChatMessageList,
  getRandomQuestion,
  Question,
  START_CHAT_URL,
  stopGenerateChat,
} from "@/services/api_question";
import { FatalError, postEventSource, PostEventSourceOptions } from "@/services/eventsource";
import { EventSourceMessage } from "@microsoft/fetch-event-source";
import { create } from "zustand";

interface ChatState {
  streamName: string;
  isGenerating: boolean;
  inputValue: string;
  messageList: ChatMessage[];
  streamError: Error | null;
  latestMessage: ChatMessage | null;
  assiociationWords: string[];
  geussQuestions: Question[];

  cancelEventSource: Function | null;

  needRetry: boolean;
}

interface StartChatParams {
  isRegenerate?: boolean;
  isContinue?: boolean;
  streamName?: string;
  message?: string;
  messageSource?: MessageSource;
  messageId?: ChatMessageId;
}

interface ChatStore extends ChatState {
  resetStore: () => void;
  setStreamName: (streamName: string) => void;
  setIsGenerating: (isGenerating: boolean) => void;
  inputValue: string;
  setInputValue: (value: string) => void;
  setMessageList: (messageList: ChatMessage[]) => void;

  getMessageList: () => Promise<void>;
  continueChat: () => void;
  startChat: (params: StartChatParams) => Function;
  getAssociationWord: (messageId: ChatMessageId) => void;
  sendQuestion: (params: StartChatParams) => Promise<void>;
  stopGenerate: () => Promise<void>;
  regenerateChat: (messageId: ChatMessageId) => void;
  getQuestions: (isInit?: boolean) => Promise<void>;
  feedBackAnswer: (params: FeedBackParams) => void;
}

const ChatState: ChatState = {
  streamName: "",
  isGenerating: false,
  inputValue: "",
  messageList: [],
  streamError: null,
  latestMessage: null,
  assiociationWords: [],
  geussQuestions: [],

  cancelEventSource: null,
  needRetry: false,
};

const downMessage = () => {
  const { latestMessage, messageList } = ChatStore.getState();
  if (latestMessage) {
    const newList = [...messageList, latestMessage as ChatMessage];
    ChatStore.setState({ messageList: newList });
    ChatStore.setState({ latestMessage: null });
  }
};

const ChatStore = create<ChatStore>()((set, get) => ({
  ...ChatState,
  resetStore: () => {
    const { cancelEventSource } = get();
    cancelEventSource?.();
    set(ChatState);
  },

  setStreamName: (streamName) => {
    set({ streamName });
  },
  setIsGenerating: (isGenerating) => {
    set({ isGenerating });
  },
  setInputValue: (value) => {
    set({ inputValue: value });
  },
  setMessageList: (messageList) => {
    set({ messageList });
  },

  getMessageList: async () => {
    const { streamName, continueChat } = get();

    try {
      const res = await getChatMessageList(streamName);
      set({ messageList: res.data });
      const length = res.data.length;
      if (length > 0) {
        if (res.data[length - 1].messageStatus === MessageStatus.PROCESSING) {
          continueChat();
        }
      }
    } catch (e) {
      console.error("getMessageList error >>>> ", e);
    }
  },
  getAssociationWord: (messageId) => {
    generateAssociationWord({ messageId }).then((res) => {
      set({ assiociationWords: res.data });
    });
  },

  startChat: (params: StartChatParams) => {
    const { messageList } = get();
    const { streamName, message, messageSource, messageId, isContinue = false, isRegenerate = false } = params;

    if (!isContinue) {
      const testContent: ChatMessage = generateEmptyMessage({
        message: "",
        messageType: MessageType.ANSWER,
      });
      set({ latestMessage: testContent });
    }

    const events: PostEventSourceOptions<EventSourceMessage> = {
      onsuccess: () => {
        const { latestMessage } = ChatStore.getState();
        if (isContinue) {
          set({
            latestMessage: {
              ...latestMessage,
              message: "",
            } as ChatMessage,
          });
        }
      },
      onmessage: (eventSource) => {
        const { latestMessage, messageList, getAssociationWord } = ChatStore.getState();
        const { content, event } = JSON.parse(eventSource.data) as ChatMessageEventSource;
        if (event === "DONE") {
          set({ isGenerating: false });
          if (latestMessage) {
            const message = {
              ...latestMessage,
              message: (latestMessage?.message || "") + content,
              messageStatus: MessageStatus.COMPLETE,
            };
            const newList = [...messageList, message as ChatMessage];
            set({ messageList: newList });
            set({ latestMessage: null });
            getAssociationWord(message.id);
          }
          return;
        }
        let message: ChatMessage | null = null;

        if (!latestMessage) {
          const ConnectData: ConnectData = JSON.parse(content);
          message = {
            ...generateEmptyMessage({
              id: ConnectData.answerId,
            }),
          };
          set({ latestMessage: message });
          return;
        }

        if (event === "CONNECT") {
          const ConnectData: ConnectData = JSON.parse(content);
          set({
            latestMessage: {
              ...latestMessage,
              id: ConnectData.answerId,
            } as ChatMessage,
          });
        }

        if (event === "REPLY") {
          set({
            latestMessage: {
              ...latestMessage,
              message: (latestMessage?.message || "") + content,
            } as ChatMessage,
          });
        }
      },
      onerror: (error) => {
        const { latestMessage, continueChat, needRetry } = ChatStore.getState();
        console.log("onerror sse", error, latestMessage, needRetry);
        set({ streamError: error as Error });
        // downMessage();
        // 如果是正常或者重新生成，并且没有最后一条回答的话，就进行重试
        if (latestMessage?.message && needRetry) {
          continueChat();
          setTimeout(() => {
            set({ needRetry: false });
          }, 10);
          throw new FatalError();
        }
      },
      onclose() {
        console.log("omclose sse");
        downMessage();
      },
    };
    set({ isGenerating: true });

    if (isRegenerate) {
      // 删掉最后一个回答
      set({ messageList: messageList.slice(0, messageList.length - 1) });
      return postEventSource(REGENERATE_CHAT_URL, { streamName, message, messageSource, messageId }, events);
    }
    return postEventSource(isContinue ? CONTINUE_CHAT_URL : START_CHAT_URL, { streamName, message, messageSource }, events);
  },

  stopGenerate: async () => {
    const { cancelEventSource, streamName, latestMessage } = get();
    if (latestMessage?.id) {
      stopGenerateChat(streamName, latestMessage.id);
    }
    cancelEventSource?.();
    downMessage();
    set({ isGenerating: false });
  },

  continueChat: () => {
    const { messageList, startChat, streamName, latestMessage } = get();
    const lastMessage = messageList[messageList.length - 1];

    console.log("继续生成的一条回答", lastMessage);
    // 如果为生成中的话
    if (lastMessage.messageStatus === MessageStatus.PROCESSING) {
      set({ messageList: messageList.slice(0, messageList.length - 1) });
      set({ latestMessage: lastMessage, isGenerating: true });
      set({ cancelEventSource: startChat({ streamName, isContinue: true }) });
    } else if (latestMessage?.messageStatus === MessageStatus.PROCESSING && latestMessage.message) {
      set({ cancelEventSource: startChat({ streamName, isContinue: true }) });
    } else {
      return;
    }
  },

  sendQuestion: async ({ message, messageSource, messageId, isRegenerate = false }) => {
    const { cancelEventSource, isGenerating, stopGenerate, startChat, streamName } = get();

    set({ needRetry: true });

    if (isGenerating) {
      await stopGenerate();
    }
    cancelEventSource?.();

    set({ assiociationWords: [] });

    const myContent: ChatMessage = generateEmptyMessage({
      message,
      messageType: MessageType.QUESTION,
      messageStatus: MessageStatus.COMPLETE,
    });

    setTimeout(() => {
      if (!isRegenerate) {
        const { messageList } = ChatStore.getState();
        const newList = [...messageList, myContent];
        set({ messageList: newList });
      }

      set({ cancelEventSource: startChat({ streamName, message, messageSource, messageId, isRegenerate }) });
    });
  },

  getQuestions: async (isInit = true) => {
    const { streamName } = get();
    try {
      const res = await getRandomQuestion({
        streamName,
        isInit,
      });
      set({ geussQuestions: res.data });
    } catch (e) {
      console.log("getQuestions error >>>> ", e);
    }
  },

  regenerateChat: (messageId) => {
    set({ isGenerating: true });
    const { sendQuestion } = get();
    sendQuestion({ isRegenerate: true, messageId });
  },

  feedBackAnswer: (params: FeedBackParams) => {
    return feedBackChat(params);
  },
}));

export default ChatStore;
